import { DestroyRef, Service, Injectable, PLATFORM_ID, computed, inject, signal, NgZone, ApplicationRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { catchError, switchMap, filter, first, delay } from 'rxjs/operators';
import { of, timer, from } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import siteConfig from '@utils/app.config';

import { NowPlayingData, NowPlayingState } from '@interfaces/now-playing.interface';

const EMPTY_TRACK = {
	artist: { mbid: '', '#text': '' },
	streamable: 0,
	image: [],
	mbid: '',
	album: { mbid: '', '#text': '' },
	name: '',
	'@attr': { nowplaying: false },
	url: ''
} as const;

const EMPTY_STATE: NowPlayingState = {
	isPlaying: false,
	songLink: null,
	nowPlayingImage: null,
	nowPlayingData: { ...EMPTY_TRACK }
};

@Service()
export class NowPlayingService {
	private http = inject(HttpClient);
	private readonly destroyRef = inject(DestroyRef);
	private readonly platformId = inject(PLATFORM_ID);
	private readonly ngZone = inject(NgZone);
	private readonly appRef = inject(ApplicationRef);
	private readonly isBrowser = isPlatformBrowser(this.platformId);

	private readonly nowPlayingUrl =
		`https://ws.audioscrobbler.com/2.0/?method=user.getrecenttracks&user=dartegnian&api_key=${siteConfig.LASTFM_API_KEY}&format=json&limit=1`;

	// ---- Signal-backed state ----
	private readonly _state = signal<NowPlayingState>({ ...EMPTY_STATE });
	private readonly _nowPlayingResponse = signal<any | null>(null);
	private pollingStarted = false;

	private readonly resolveLinkPlatformLabel = (link: string | null): string => {
		if (!link) return 'Spotify';
		if (link.includes('last.fm')) return 'Last.fm';
		if (link.includes('spotify.com')) return 'Spotify';
		return 'Link';
	};

	readonly isPlayingSignal = computed(() => this._state().isPlaying);
	readonly songLinkSignal = computed(() => this._state().songLink);
	readonly nowPlayingImageSignal = computed(() => this._state().nowPlayingImage);
	readonly nowPlayingDataSignal = computed(() => this._state().nowPlayingData);
	readonly nowPlayingResponseSignal = this._nowPlayingResponse.asReadonly();

	readonly nowPlayingTitle = computed(() => this._state().nowPlayingData?.['name'] ?? '');
	readonly nowPlayingArtist = computed(() => this._state().nowPlayingData?.['artist']?.['#text'] ?? '');
	readonly nowPlayingAlbum = computed(() => this._state().nowPlayingData?.['album']?.['#text'] ?? '');

	readonly musicProviderLabel = computed(() => this.resolveLinkPlatformLabel(this._state().songLink));
	readonly linkActionLabel = computed(() => {
		const link = this._state().songLink;
		if (!link) return '';
		return `Listen on ${this.musicProviderLabel()}`;
	});
	readonly linkTooltipText = computed(() => {
		const link = this._state().songLink;
		if (!link) return null;
		const title = this.nowPlayingTitle();
		const platform = this.musicProviderLabel();
		return title ? `Listen to ${title} on ${platform}` : `Listen on ${platform}`;
	});


	getCurrentInfo() {
		return from(fetch(this.nowPlayingUrl, { credentials: 'omit', mode: 'cors' }).then(res => {
			if (!res.ok) throw new Error('Network response was not ok');
			return res.json();
		}));
	}

	startPolling(): void {
		if (!this.isBrowser || this.pollingStarted) return;
		this.pollingStarted = true;

		this.appRef.isStable.pipe(
			filter(stable => stable),
			first(),
			delay(3000)
		).subscribe(() => {
			this.ngZone.runOutsideAngular(() => {
				timer(0, 15000).pipe(
					switchMap(() => this.getCurrentInfo().pipe(
						catchError(err => {
							console.error('Now playing fetch failed:', err);
							return of(null);
						}),
					)),
					takeUntilDestroyed(this.destroyRef),
				).subscribe((data) => {
					this.ngZone.run(() => {
						this._nowPlayingResponse.set(data);
					});
				});
			});
		});
	}

	setNowPlayingState(state: NowPlayingState): void {
		const previousImage = this._state().nowPlayingImage;
		this.revokeObjectUrlIfNeeded(previousImage, state.nowPlayingImage);
		this._state.set(state);
	}

	patchNowPlayingState(patch: Partial<NowPlayingState>): void {
		this._state.update((prev) => {
			const next = { ...prev, ...patch };
			this.revokeObjectUrlIfNeeded(prev.nowPlayingImage, next.nowPlayingImage);
			return next;
		});
	}

	setIsPlaying(isPlaying: boolean): void {
		this.patchNowPlayingState({ isPlaying });
	}

	setSongLink(songLink: string | null): void {
		this.patchNowPlayingState({ songLink });
	}

	setNowPlayingImage(nowPlayingImage: string | null): void {
		this.patchNowPlayingState({ nowPlayingImage });
	}

	setNowPlayingData(nowPlayingData: NowPlayingData): void {
		this.patchNowPlayingState({ nowPlayingData });
	}

	reset(): void {
		const previous = this._state().nowPlayingImage;
		this.revokeObjectUrlIfNeeded(previous, null);
		this._state.set({ ...EMPTY_STATE });
	}

	private revokeObjectUrlIfNeeded(previous: string | null, next: string | null): void {
		if (previous && previous.startsWith('blob:') && previous !== next) {
			try {
				if (typeof URL !== 'undefined' && typeof (URL as any).revokeObjectURL === 'function') {
					(URL as any).revokeObjectURL(previous);
				}
			} catch { }
		}
	}
}
